import "core-js/modules/web.dom.iterable.js";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
/* eslint-disable max-len */
/* eslint-disable react-hooks/rules-of-hooks */
import { useContext, useEffect, useRef, useMemo } from 'react';
import { useStoreId } from '@thd-nucleus/experience-context';
import { useLazyQuery, useMutation } from '@apollo/client';
import { useQueryAttributes } from "./useQueryAttributes.js";
import { getQueryArgs, getQueryFromAttributes, getQueryType } from "./getQueryFromAttributes.js";
import { useQuery } from "../../hooks/useQuery.js";
import QueryContext from "../QueryContext.js";
import { getQueryKey, setNewDataInStorage } from "../util.js";
const defaultOpts = {
  ssr: true,
  errorPolicy: 'all'
};

// these two function will move to experience as options on DataProvider
const useIsMounted = () => {
  const storeId = useStoreId();
  return storeId !== '8119';
};

// @todo consider using below
// const useIsMounted = () => {
//   const { isLocalized, storeId } = useStore();
//   if (typeof isLocalized === 'undefined') {
//     return storeId !== '8119';
//   }
//
//   return isLocalized;
// };

const shouldSkip = _ref => {
  var _options$variables, _options$variables2;
  let {
    options,
    mounted,
    persist
  } = _ref;
  if (persist !== null && persist !== void 0 && persist.hasPersist && !(persist !== null && persist !== void 0 && persist.ready)) return true;

  // skip renders until
  if ((_options$variables = options.variables) !== null && _options$variables !== void 0 && _options$variables.storeId && options.ssr === false && !mounted) {
    // eslint-disable-next-line no-param-reassign
    return true;
  }
  if (((_options$variables2 = options.variables) === null || _options$variables2 === void 0 ? void 0 : _options$variables2.storeId) === '8119' && mounted) {
    return true;
  }
  return false;
};
const defaultMountFn = _ref2 => {
  let {
    mounted
  } = _ref2;
  return mounted;
};
const defaultSkipFn = _ref3 => {
  let {
    skip
  } = _ref3;
  return skip;
};
const defaultOptionsFn = _ref4 => {
  let {
    options
  } = _ref4;
  return options;
};
export const useDataModel = function (queryName) {
  var _response$;
  let opts = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : defaultOpts;
  let lazy = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;
  const options = _objectSpread(_objectSpread({}, defaultOpts), opts);
  const {
    dataSource,
    defaultVariables,
    queryAttributes = {},
    mounted: mountedFn = defaultMountFn,
    skip: skipFn = defaultSkipFn,
    queryOptions: optionsFn = defaultOptionsFn,
    merge,
    cacheKey: queryProviderCacheKey,
    onResponse,
    persist: persistCtx,
    dataStore,
    forceClientQueriesOnServer: forceClientQueriesOnServerFromContext
  } = useContext(QueryContext);
  const mutationVariablesRef = useRef();
  const forceClientQueriesOnServer = options.forceClientQueriesOnServer || (forceClientQueriesOnServerFromContext || []).includes(queryName);

  // method to wait for client render
  const mounted = forceClientQueriesOnServer || mountedFn({
    mounted: typeof options.mounted !== 'undefined' ? options.mounted : useIsMounted()
  });
  const variables = options.variables ? _objectSpread({}, options.variables) : {};
  const {
    persist
  } = options;
  const ssr = !persist && options.ssr && !mounted;
  if (merge) {
    // eslint-disable-next-line no-param-reassign
    const keys = Object.keys(merge);
    const allQueryKeys = !mounted ? Object.keys(queryAttributes.server || {}) : Object.keys(queryAttributes.client || {});

    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < keys.length; i++) {
      const key = keys[i];
      const ret = merge[key].fn({
        query: queryName,
        ssr: options.ssr,
        mounted,
        allQueryKeys
      });
      if (ret !== queryName) {
        // eslint-disable-next-line no-param-reassign
        queryName = ret;
        break;
      }
    }
  }
  if (persist && !(persistCtx !== null && persistCtx !== void 0 && persistCtx.hasPersist)) {
    throw new Error('persist must be set on the query provider');
  }
  const {
    storage: storageLocation,
    expirationInSeconds
  } = persist || {};
  const queryKey = useMemo(() => getQueryKey(queryName, variables), [queryName, variables]);
  const {
    attributes,
    queryName: aliasedQueryName
  } = useQueryAttributes({
    query: queryName,
    ssr,
    dataModel: opts.dataModel
  });
  options.skip = skipFn({
    skip: options.skip || shouldSkip({
      options,
      mounted,
      persist: _objectSpread(_objectSpread({}, persistCtx), persist)
    }),
    queryName,
    attributes
  });
  const isomorphicQueryType = ssr && !forceClientQueriesOnServer ? 'server' : 'client';
  if (!queryAttributes[isomorphicQueryType]) {
    queryAttributes[isomorphicQueryType] = {};
  }
  if (!queryAttributes[isomorphicQueryType][queryName]) {
    queryAttributes[isomorphicQueryType][queryName] = {};
  }
  let query;
  if (queryAttributes[isomorphicQueryType][queryName]._cachedQuery && !opts.dataModel && queryProviderCacheKey) {
    query = queryAttributes[isomorphicQueryType][queryName]._cachedQuery;
  } else {
    query = getQueryFromAttributes({
      name: queryName,
      attributes,
      ssr: options.ssr && !mounted
    });
    if (queryProviderCacheKey) {
      queryAttributes[isomorphicQueryType][queryName]._cachedQuery = query;
    }
  }
  let args;
  if (queryAttributes[isomorphicQueryType][queryName]._cachedArgs && !opts.dataModel && queryProviderCacheKey) {
    args = queryAttributes[isomorphicQueryType][queryName]._cachedArgs;
  } else {
    args = getQueryArgs({
      attributes,
      ssr,
      asString: false
    });
    if (queryProviderCacheKey) {
      queryAttributes[isomorphicQueryType][queryName]._cachedArgs = args;
    }
  }
  const queryType = getQueryType({
    attributes,
    name: queryName
  });

  // if args contain more than all variables
  const missingVariables = Object.keys(args || {}).filter(key => {
    return typeof (variables === null || variables === void 0 ? void 0 : variables[key]) === 'undefined';
  });
  if (missingVariables.length) {
    missingVariables.forEach(key => {
      if (dataSource && key === 'dataSource') {
        variables.dataSource = dataSource;
      }
      if (typeof defaultVariables.current[key] !== 'undefined') {
        variables[key] = defaultVariables.current[key];
      } else if (args[key]._isRequired && queryType !== 'mutation' && !lazy && !options.skip) {
        throw new Error(`'${key}' is missing from options.variables in useDataModel hook.
This likely due to one of the following reasons:
  1. A parameter was included when building the component dataModel but not passed into options.variables.
  2. The component dataModel was merged with another component requesting the '${key}' using params(). Implement defaultVaribles.${key} attribute in the QueryProvider to avoid this error.`);
      }
    });
  }

  // sort variables alphabetically for matching queries
  const sortedKeys = Object.keys(variables).sort();
  options.variables = sortedKeys.reduce((acc, cur) => {
    return _objectSpread(_objectSpread({}, acc), {}, {
      [cur]: variables[cur]
    });
  }, {});

  // @todo remove this business logic, should be controlled by queryprovider
  if (!options.fetchPolicy && dataSource && dataSource !== 'catalog') {
    options.fetchPolicy = 'cache-only';
  }
  const cachedVariables = _objectSpread({}, options.variables || {});
  const optionsForQuery = optionsFn({
    options,
    queryName
  });
  if (lazy && queryType !== 'mutation') {
    const [exec, resp] = useLazyQuery(query, optionsForQuery);
    if (resp.data) {
      if (ssr) {
        queryAttributes.server[queryName]._isResolved = true;
      } else if (!resp.loading) {
        queryAttributes.client[queryName]._isResolved = true;
      }
      if (storageLocation) {
        setNewDataInStorage({
          attributes,
          expirationInSeconds,
          queryKey,
          queryName,
          data: resp.data,
          options: optionsForQuery,
          storage: storageLocation
        });
      }
    }
    return [exec, resp, cachedVariables];
  }
  const response = queryType === 'mutation' ? useMutation(query, optionsForQuery) : useQuery(query, optionsForQuery);
  useEffect(() => {
    // Mutation returns a tuple while queries return object.
    const responseObject = queryType === 'mutation' ? response[1] : response;
    if (typeof window !== 'undefined' && window.LIFE_CYCLE_EVENT_BUS && responseObject !== null && responseObject !== void 0 && responseObject.data && !(responseObject !== null && responseObject !== void 0 && responseObject.loading)) {
      var _mutationVariablesRef;
      const {
        data,
        variables: responseVariables
      } = responseObject;
      const eventBusVariables = queryType === 'mutation' ? _objectSpread(_objectSpread({}, optionsForQuery === null || optionsForQuery === void 0 ? void 0 : optionsForQuery.variables), ((_mutationVariablesRef = mutationVariablesRef.current) === null || _mutationVariablesRef === void 0 ? void 0 : _mutationVariablesRef.variables) || {}) : responseVariables;
      window.LIFE_CYCLE_EVENT_BUS.trigger('data-sources.data', {
        type: 'product',
        queryName,
        variables: eventBusVariables,
        data
      });
    }
    if (typeof window !== 'undefined' && window.LIFE_CYCLE_EVENT_BUS && responseObject !== null && responseObject !== void 0 && responseObject.error && !(responseObject !== null && responseObject !== void 0 && responseObject.loading)) {
      var _mutationVariablesRef2;
      const {
        data,
        error,
        variables: responseVariables
      } = responseObject;
      const eventBusVariables = queryType === 'mutation' ? _objectSpread(_objectSpread({}, optionsForQuery === null || optionsForQuery === void 0 ? void 0 : optionsForQuery.variables), ((_mutationVariablesRef2 = mutationVariablesRef.current) === null || _mutationVariablesRef2 === void 0 ? void 0 : _mutationVariablesRef2.variables) || {}) : responseVariables;
      window.LIFE_CYCLE_EVENT_BUS.trigger('data-sources.error', {
        type: 'product',
        queryName,
        variables: eventBusVariables,
        data,
        message: `GQL Error: ${queryName} - ${error === null || error === void 0 ? void 0 : error.message}`,
        parentTraceId: window.THD_PARENT_TRACE_ID,
        traceId: window.THD_TRACE_ID
      });
    }
  }, [response === null || response === void 0 ? void 0 : response.loading, response === null || response === void 0 ? void 0 : (_response$ = response[1]) === null || _response$ === void 0 ? void 0 : _response$.loading]);
  if (queryType === 'mutation') {
    const [exec, responseObj] = response;
    const customExec = execOptions => {
      mutationVariablesRef.current = execOptions;
      return exec(execOptions);
    };
    return [customExec, responseObj];
  }
  if (optionsForQuery.dataModel) {
    delete optionsForQuery.dataModel;
  }
  if (typeof optionsForQuery.mounted !== 'undefined') {
    delete optionsForQuery.mounted;
  }

  // apollo client is getting overwritten and not providing access to latest.
  if (typeof window !== 'undefined' && window.__APOLLO_CLIENT__) {
    window.__APOLLO_CLIENT__ = response.client;
  }
  if (response.data) {
    if (dataStore) {
      Object.keys(response.data).forEach(key => {
        dataStore.current[key] = response.data[key];
      });
    }
    if (onResponse) {
      onResponse({
        response,
        queryName
      });
    }
    if (storageLocation) {
      setNewDataInStorage({
        attributes,
        expirationInSeconds,
        queryKey,
        queryName,
        data: response.data,
        options: optionsForQuery,
        storage: storageLocation
      });
    }
    if (ssr) {
      queryAttributes.server[queryName]._isResolved = true;
    } else if (!response.loading) {
      queryAttributes.client[queryName]._isResolved = true;
    }
  }
  return response;
};
//# sourceMappingURL=useDataModel.js.map